import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import MessageBoard from './MessageBoard';
import RaceReviews from './RaceReviews';
import LoadingScreen from './LoadingScreen';
import UserCard from './UserCard';
import UserSearch from './UserSearch';
import { formatDate } from './utils';
import RaceStories from './RaceStories';
import AchillesBadge from './AchillesBadge';
import HotelSearch from './HotelSearch';
import api from './api';
import useIsMobile from './hooks/useIsMobile';

const CommunityDetail = ({ community, onBack, user, onLoginRequired, onViewProfile }) => {
  const isCity = community.communityType === 'city';
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('about');
  const isMobile = useIsMobile();
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [followingSet, setFollowingSet] = useState(new Set());
  const [followedRunners, setFollowedRunners] = useState([]);
  const [memberTags, setMemberTags] = useState({});
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [communityId, setCommunityId] = useState(community.communityId || null);
  const [cityData, setCityData] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportType, setReportType] = useState('wrong_date');
  const [reportMessage, setReportMessage] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [achillesGuides, setAchillesGuides] = useState([]);

  useEffect(() => {
    if (isCity) {
      lookupCityCommunity();
    } else {
      setLoading(false);
      if (community.communityId) {
        setCommunityId(community.communityId);
      } else {
        lookupCommunityId();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [community]);

  const lookupCityCommunity = async () => {
    try {
      const res = await api.get(`/communities/city/${encodeURIComponent(community.cityName)}`);
      const running = res.data.find(c => c.sport === 'running');
      if (running) {
        setCommunityId(running.id);
        setCityData(running);
      }
    } catch (err) {
      // City community may not exist yet
    } finally {
      setLoading(false);
    }
  };

  const lookupCommunityId = async () => {
    try {
      const res = await api.get(`/communities?race_id=${community.id}`);
      if (res.data.length > 0) {
        setCommunityId(res.data[0].id);
      }
    } catch (err) {
      // Community may not exist yet (user not registered)
    }
  };

  useEffect(() => {
    if (activeTab === 'members' && user) {
      fetchMembers();
    }
    if (activeTab === 'message-boards' && communityId) {
      fetchBoards();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, community.id, communityId]);

  const fetchBoards = async () => {
    if (!communityId) return;
    setBoardsLoading(true);
    try {
      const res = await api.get(`/communities/${communityId}/boards`);
      setBoards(res.data);
    } catch (err) {
      console.error('Error loading boards:', err);
    } finally {
      setBoardsLoading(false);
    }
  };

  const fetchMembers = async () => {
    if (!user) return;
    setMembersLoading(true);
    try {
      if (isCity && communityId) {
        // City community: fetch members via community membership
        const [membersRes, followingRes] = await Promise.all([
          api.get(`/communities/${communityId}/members`),
          api.get('/follows/following'),
        ]);
        setMembers(membersRes.data);
        const fSet = new Set(followingRes.data.map(u => u.id));
        setFollowingSet(fSet);
        setFollowedRunners(membersRes.data.filter(m => fSet.has(m.id)));

        const ids = membersRes.data.map(u => u.id);
        if (ids.length > 0) {
          const tagsRes = await api.get(`/tags/users?ids=${ids.join(',')}`);
          setMemberTags(tagsRes.data);
        }
      } else {
        const [membersRes, followingRes, overlapRes] = await Promise.all([
          api.get(`/registrations/race/${community.id}/users`),
          api.get('/follows/following'),
          api.get(`/follows/race-overlaps/${community.id}`),
        ]);
        setMembers(membersRes.data);
        const fSet = new Set(followingRes.data.map(u => u.id));
        setFollowingSet(fSet);
        setFollowedRunners(overlapRes.data);

        const ids = membersRes.data.map(u => u.id);
        if (ids.length > 0) {
          const tagsRes = await api.get(`/tags/users?ids=${ids.join(',')}`);
          setMemberTags(tagsRes.data);
        }
      }
      // Load achilles guides for race communities
      if (!isCity) {
        try {
          const guidesRes = await api.get(`/achilles/guides/race/${community.id}`);
          setAchillesGuides(guidesRes.data);
        } catch (e) { /* non-critical */ }
      }
    } catch (err) {
      console.error('Error loading members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  const handleToggleFollow = async (userId) => {
    const isFollowing = followingSet.has(userId);
    try {
      if (isFollowing) {
        await api.delete(`/follows/${userId}`);
        setFollowingSet(prev => { const next = new Set(prev); next.delete(userId); return next; });
        setFollowedRunners(prev => prev.filter(u => u.id !== userId));
      } else {
        await api.post(`/follows/${userId}`);
        setFollowingSet(prev => new Set(prev).add(userId));
        const newRunner = members.find(m => m.id === userId);
        if (newRunner) setFollowedRunners(prev => [...prev, newRunner]);
      }
    } catch (err) {
      console.error('Toggle follow error:', err);
    }
  };

  const submitReport = async () => {
    if (!user) { onLoginRequired && onLoginRequired(); return; }
    setReportSubmitting(true);
    try {
      await api.post(`/races/${community.id}/report`, {
        report_type: reportType,
        message: reportMessage || null,
      });
      setReportSubmitted(true);
      setTimeout(() => {
        setShowReportModal(false);
        setReportSubmitted(false);
        setReportMessage('');
        setReportType('wrong_date');
      }, 2000);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit report';
      alert(msg);
    } finally {
      setReportSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Loading community..." />;
  }

  if (!community) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: '#0B0B10',
        color: '#FFFFFF'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Community not found</h2>
          <button onClick={onBack} style={{
            padding: '0.75rem 1.5rem',
            background: '#FF8A3D',
            color: '#0B0B10',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 600,
            marginTop: '1rem'
          }}>
            Back to Communities
          </button>
        </div>
      </div>
    );
  }

  // If a board is selected, show the MessageBoard component
  if (selectedBoard) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0B0B10',
        fontFamily: 'Inter, sans-serif'
      }}>
        {/* Header */}
        <div style={{
          background: '#0B0B10',
          padding: isMobile ? '1rem' : '1.5rem 2rem',
          borderBottom: '1px solid rgba(255, 138, 61, 0.15)'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <button
              onClick={() => setSelectedBoard(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#FF8A3D',
                cursor: 'pointer',
                fontSize: '1.5rem',
                padding: '0.5rem'
              }}
            >
              ←
            </button>
            <div>
              <p style={{ color: '#8b9cb3', margin: 0, fontSize: '0.85rem' }}>
                {community.name}
              </p>
              <h1 style={{
                fontSize: '1.5rem',
                color: '#FFFFFF',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {selectedBoard.icon || '💬'} {selectedBoard.name}
              </h1>
            </div>
          </div>
        </div>
        
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '1rem' : '2rem' }}>
          <MessageBoard
            board={selectedBoard}
            communityName={community.name}
            onBack={() => setSelectedBoard(null)}
            user={user}
            onLoginRequired={onLoginRequired}
            onViewProfile={onViewProfile}
          />
        </div>
      </div>
    );
  }

  // --- City community view ---
  if (isCity) {
    const cityName = community.cityName;
    const cityTabs = ['About', 'Members', 'Message Boards'];

    return (
      <div style={{
        minHeight: '100vh',
        background: '#0B0B10',
        fontFamily: 'Inter, sans-serif'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0B0B10 0%, #1E1E28 100%)',
          padding: isMobile ? '1rem' : '1.5rem 2rem',
          borderBottom: '1px solid rgba(255, 138, 61, 0.2)'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <button
              onClick={onBack}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#FF8A3D',
                cursor: 'pointer',
                fontSize: '1.5rem',
                padding: '0.5rem'
              }}
            >
              {'\u2190'}
            </button>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'Instrument Serif, serif',
              color: '#FFFFFF',
              margin: 0
            }}>
              {cityName} Running
            </h1>
          </div>
        </div>

        {/* Gradient Hero (no race image) */}
        <div style={{
          width: '100%',
          height: '200px',
          background: 'linear-gradient(135deg, #1E1E28 0%, #0B0B10 50%, #1E1E28 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>{'\u{1F3C3}'}</div>
            <h2 style={{
              fontSize: '2rem',
              color: '#FFFFFF',
              margin: 0,
              fontWeight: 700,
              background: 'linear-gradient(135deg, #FF8A3D 0%, #FFFFFF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              {cityName} Running
            </h2>
            {cityData && (
              <p style={{ color: '#8b9cb3', margin: '0.5rem 0 0', fontSize: '0.95rem' }}>
                {cityData.member_count || 0} {cityData.member_count === 1 ? 'member' : 'members'}
              </p>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: isMobile ? '1rem' : '2rem'
        }}>
          {/* Tabs */}
          <div role="tablist" aria-label="Community sections" style={{
            display: 'flex',
            gap: isMobile ? '1rem' : '2rem',
            borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
            marginBottom: isMobile ? '1rem' : '2rem',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}>
            {cityTabs.map((tab) => {
              const tabKey = tab.toLowerCase().replace(' ', '-');
              const isActive = activeTab === tabKey;
              return (
                <button
                  key={tabKey}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tabKey)}
                  style={{
                    padding: isMobile ? '0.75rem 0' : '1rem 0',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: isActive ? '2px solid #FF8A3D' : '2px solid transparent',
                    color: isActive ? '#FF8A3D' : '#9ca3af',
                    cursor: 'pointer',
                    fontSize: isMobile ? '0.85rem' : '1rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {tab}
                </button>
              );
            })}
          </div>

          {/* About */}
          {activeTab === 'about' && (
            <div>
              <h3 style={{ color: '#FFFFFF', fontSize: '1.5rem', marginBottom: '1rem' }}>
                About {cityName} Running
              </h3>
              <p style={{ color: '#9ca3af', fontSize: '1.1rem', lineHeight: 1.8, marginBottom: '2rem' }}>
                {cityData?.description || `Connect with runners in ${cityName}. Find training partners, discover local races, share routes, and be part of the running community.`}
              </p>
            </div>
          )}

          {/* Members */}
          {activeTab === 'members' && (
            <div>
              {!user ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 138, 61, 0.15)'
                }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{'\u{1F465}'}</div>
                  <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>Sign in to see members</h3>
                  <p style={{ color: '#9ca3af', marginBottom: '1rem' }}>See who else is running in {cityName}.</p>
                  <button onClick={() => onLoginRequired && onLoginRequired()} style={{ padding: '0.6rem 1.5rem', background: '#FF8A3D', border: 'none', color: '#0B0B10', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Sign In</button>
                </div>
              ) : (
                <>
                  <UserSearch onViewProfile={onViewProfile} currentUserId={user.id} />

                  {followedRunners.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h4 style={{ color: '#FF8A3D', fontSize: '1rem', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        Runners You Follow
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '1rem', background: 'rgba(255, 138, 61, 0.05)', border: '1px solid rgba(255, 138, 61, 0.2)', borderRadius: '12px' }}>
                        {followedRunners.map(runner => (
                          <UserCard key={runner.id} user={runner} isFollowing={true} onToggleFollow={handleToggleFollow} onViewProfile={onViewProfile} tags={memberTags[runner.id] || []} currentUserId={user.id} />
                        ))}
                      </div>
                    </div>
                  )}

                  <h4 style={{ color: '#FFFFFF', fontSize: '1rem', marginBottom: '0.75rem' }}>
                    Community Members ({members.length})
                  </h4>
                  {membersLoading ? (
                    <p role="status" aria-live="polite" style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>Loading members...</p>
                  ) : members.length === 0 ? (
                    <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>No members yet. Be the first to join!</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {members.map(member => (
                        <UserCard key={member.id} user={member} isFollowing={followingSet.has(member.id)} onToggleFollow={handleToggleFollow} onViewProfile={onViewProfile} tags={memberTags[member.id] || []} currentUserId={user.id} />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Message Boards */}
          {activeTab === 'message-boards' && (
            <div>
              <h3 style={{ color: '#FFFFFF', fontSize: '1.5rem', marginBottom: '1rem' }}>
                Discussion Boards
              </h3>
              {!communityId ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 138, 61, 0.15)'
                }}>
                  <p style={{ color: '#8b9cb3' }}>Join this community to access its message boards.</p>
                </div>
              ) : boardsLoading ? (
                <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>Loading boards...</p>
              ) : (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 138, 61, 0.15)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  padding: '0.5rem'
                }}>
                  {boards.length === 0 ? (
                    <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '1.5rem', margin: 0 }}>
                      No boards yet for this community.
                    </p>
                  ) : (
                    boards.map((board) => (
                      <div
                        key={board.id}
                        onClick={() => setSelectedBoard(board)}
                        style={{
                          padding: '1rem 1.25rem',
                          margin: '0.25rem',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'background 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                          <span style={{ fontSize: '1.25rem' }}>{board.icon || '\u{1F4AC}'}</span>
                          <div>
                            <h4 style={{ color: '#FFFFFF', margin: 0, fontSize: '1rem', fontWeight: 500 }}>
                              {board.name}
                            </h4>
                            <p style={{ color: '#6b7280', margin: '0.15rem 0 0 0', fontSize: '0.8rem' }}>
                              {board.thread_count} {board.thread_count === 1 ? 'thread' : 'threads'}
                            </p>
                          </div>
                        </div>
                        <span style={{ color: '#FF8A3D', fontSize: '1rem' }}>{'\u2192'}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- Race community view (original) ---
  const raceYear = community.end_date ? new Date(community.end_date).getFullYear() : new Date().getFullYear();
  // Many community names already include the year (e.g. "BMW Berlin Marathon 2026"),
  // so only append if it's missing to avoid "BMW Berlin Marathon 2026 2026".
  const nameHasYear = community.name && new RegExp(`\\b${raceYear}\\b`).test(community.name);
  const seoTitle = nameHasYear
    ? `${community.name}: Where to Stay, Travel Guide & Community`
    : `${community.name} ${raceYear}: Where to Stay, Travel Guide & Community`;
  const seoDesc = community.description
    ? `${community.description.slice(0, 140)}... Find hotels, travel tips, and connect with other runners on Runrora.`
    : `Planning to run ${community.name} in ${community.location}? Find hotels near the start, travel tips, pace strategy, and connect with other runners on Runrora.`;
  const seoImage = community.image_url || 'https://runrora.com/og-image.png';

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0B10',
      fontFamily: 'Inter, sans-serif'
    }}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://runrora.com/race/${community.id}`} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'SportsEvent',
          name: community.name,
          startDate: community.end_date,
          location: {
            '@type': 'Place',
            name: community.location,
            address: community.location,
          },
          description: community.description || seoDesc,
          image: community.image_url,
          url: `https://runrora.com/race/${community.id}`,
          organizer: {
            '@type': 'Organization',
            name: 'Runrora',
            url: 'https://runrora.com',
          },
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          eventStatus: 'https://schema.org/EventScheduled',
        })}</script>
      </Helmet>

      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0B0B10 0%, #1E1E28 100%)',
        padding: isMobile ? '1rem' : '1.5rem 2rem',
        borderBottom: '1px solid rgba(255, 138, 61, 0.2)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#FF8A3D',
              cursor: 'pointer',
              fontSize: '1.5rem',
              padding: '0.5rem'
            }}
          >
            ←
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <h1 style={{
              fontSize: '1.5rem',
              fontFamily: 'Instrument Serif, serif',
              color: '#FFFFFF',
              margin: 0
            }}>
              {community.name}
            </h1>
            {community.verified && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem',
                background: 'rgba(74, 222, 128, 0.15)',
                color: '#4ade80',
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '0.2rem 0.6rem',
                borderRadius: '12px',
                whiteSpace: 'nowrap'
              }}>
                ✓ Verified
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Hero Image */}
      <div style={{
        width: '100%',
        height: isMobile ? '200px' : '300px',
        background: `linear-gradient(rgba(11, 11, 16, 0.3), rgba(11, 11, 16, 0.7)), url(${community.image_url}) center/cover`,
        display: 'flex',
        alignItems: 'flex-end',
        padding: isMobile ? '1rem' : '2rem'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%'
        }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(255, 138, 61, 0.9)',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontSize: '0.9rem',
            color: '#0B0B10',
            fontWeight: 600,
            marginBottom: '0.5rem'
          }}>
            📍 {community.location}
          </div>
          <h2 style={{
            fontSize: '2.5rem',
            color: '#FFFFFF',
            margin: 0,
            fontWeight: 700
          }}>
            {formatDate(community.end_date, 'long')}
          </h2>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: isMobile ? '1rem' : '2rem'
      }}>
        {/* Tabs */}
        <div role="tablist" aria-label="Race sections" style={{
          display: 'flex',
          gap: isMobile ? '1rem' : '2rem',
          borderBottom: '1px solid rgba(255, 138, 61, 0.2)',
          marginBottom: isMobile ? '1rem' : '2rem',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}>
          {['About', 'Members', 'Stories', 'Reviews', 'Message Boards'].map((tab) => {
            const tabKey = tab.toLowerCase().replace(' ', '-');
            const isActive = activeTab === tabKey;

            return (
              <button
                key={tabKey}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tabKey)}
                style={{
                  padding: isMobile ? '0.75rem 0' : '1rem 0',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? '2px solid #FF8A3D' : '2px solid transparent',
                  color: isActive ? '#FF8A3D' : '#9ca3af',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.85rem' : '1rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s ease'
                }}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'about' && (
          <div>
            <h3 style={{
              color: '#FFFFFF',
              fontSize: '1.5rem',
              marginBottom: '1rem'
            }}>
              About This Race
            </h3>
            <p style={{
              color: '#9ca3af',
              fontSize: '1.1rem',
              lineHeight: 1.8,
              marginBottom: '2rem'
            }}>
              {community.description || `Join fellow runners for the ${community.name} in ${community.location}. Connect with the community, share travel tips, and make this race an unforgettable experience.`}
            </p>

            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 138, 61, 0.2)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginTop: '2rem'
            }}>
              <h4 style={{
                color: '#FF8A3D',
                fontSize: '1.2rem',
                marginBottom: '1rem'
              }}>
                Race Details
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div style={{ color: '#FFFFFF' }}>
                  <strong>Date:</strong> {formatDate(community.end_date, 'long')}
                </div>
                <div style={{ color: '#FFFFFF' }}>
                  <strong>Location:</strong> {community.location}
                </div>
                <div style={{ color: '#FFFFFF' }}>
                  <strong>Type:</strong> {community.type || 'Race'}
                </div>
                {community.external_url && community.source && community.source !== 'manual' && (
                  <div style={{ color: '#FFFFFF' }}>
                    <strong>Official Page:</strong>{' '}
                    <a
                      href={community.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#FF8A3D', textDecoration: 'none' }}
                    >
                      View on {community.source.toUpperCase()} ↗
                    </a>
                  </div>
                )}
              </div>

              {/* Report incorrect info */}
              {!isCity && (
                <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                  <button
                    onClick={() => user ? setShowReportModal(true) : onLoginRequired && onLoginRequired()}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#8b9cb3',
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                  >
                    Report incorrect info
                  </button>
                </div>
              )}
            </div>

            {/* Hotel search — only for race communities (not city communities) */}
            {!isCity && (
              <HotelSearch race={{ location: community.location, date: community.end_date }} />
            )}

            {/* Achilles Guides Available */}
            {achillesGuides.length > 0 && (
              <div style={{
                background: 'rgba(56, 189, 248, 0.06)',
                border: '1px solid rgba(56, 189, 248, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginTop: '1.5rem',
              }}>
                <h4 style={{
                  color: '#38bdf8',
                  fontSize: '1.1rem',
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}>
                  <span style={{ fontSize: '1.2rem' }}>{'\u{1F9AE}'}</span>
                  Achilles Guides Available ({achillesGuides.length})
                </h4>
                <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '1rem' }}>
                  These volunteer guides are registered to run this race and available to guide visually impaired athletes.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {achillesGuides.map(guide => {
                    const name = guide.first_name
                      ? `${guide.first_name} ${guide.last_name || ''}`.trim()
                      : guide.name;
                    const initials = guide.first_name
                      ? (guide.first_name[0] + (guide.last_name?.[0] || '')).toUpperCase()
                      : (guide.name || 'G')[0].toUpperCase();
                    return (
                      <div
                        key={guide.id}
                        onClick={() => onViewProfile && onViewProfile(guide.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '0.75rem',
                          background: 'rgba(255, 255, 255, 0.03)',
                          borderRadius: '10px',
                          cursor: 'pointer',
                        }}
                      >
                        {guide.profile_photo ? (
                          <img src={guide.profile_photo} alt={`${guide.first_name || guide.name || 'Guide'} avatar`} style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '50%',
                            background: 'rgba(56, 189, 248, 0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.85rem', fontWeight: 700, color: '#38bdf8',
                          }}>
                            {initials}
                          </div>
                        )}
                        <div style={{ flex: 1 }}>
                          <span style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.9rem' }}>{name}</span>
                          {guide.city && (
                            <span style={{ color: '#8b9cb3', fontSize: '0.75rem', marginLeft: '0.5rem' }}>{guide.city}</span>
                          )}
                        </div>
                        <AchillesBadge type="guide" compact />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Report Modal */}
            {showReportModal && (
              <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.7)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '1rem',
              }} onClick={() => setShowReportModal(false)}>
                <div style={{
                  background: '#16161D', borderRadius: '16px', padding: '2rem',
                  maxWidth: '420px', width: '100%', border: '1px solid rgba(255,138,61,0.15)',
                }} onClick={e => e.stopPropagation()}>
                  {reportSubmitted ? (
                    <div style={{ textAlign: 'center', padding: '1rem' }}>
                      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✓</div>
                      <p style={{ color: '#FFFFFF', margin: 0 }}>Thanks! We'll review your report.</p>
                    </div>
                  ) : (
                    <>
                      <h3 style={{ color: '#FF8A3D', margin: '0 0 1rem', fontSize: '1.1rem' }}>Report Incorrect Info</h3>
                      <p style={{ color: '#8b9cb3', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                        Help us keep race data accurate. What's wrong with <strong style={{ color: '#FFFFFF' }}>{community.name}</strong>?
                      </p>

                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ color: '#8b9cb3', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>Issue type</label>
                        <select
                          value={reportType}
                          onChange={e => setReportType(e.target.value)}
                          style={{
                            width: '100%', padding: '0.6rem', background: '#0B0B10', color: '#FFFFFF',
                            border: '1px solid rgba(255,138,61,0.2)', borderRadius: '8px', fontSize: '0.9rem',
                          }}
                        >
                          <option value="wrong_date">Wrong date</option>
                          <option value="wrong_location">Wrong location</option>
                          <option value="wrong_url">Wrong registration link</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div style={{ marginBottom: '1.5rem' }}>
                        <label style={{ color: '#8b9cb3', fontSize: '0.8rem', display: 'block', marginBottom: '0.5rem' }}>Details (optional)</label>
                        <textarea
                          value={reportMessage}
                          onChange={e => setReportMessage(e.target.value)}
                          placeholder="e.g., The correct date is April 15, 2026"
                          rows={3}
                          style={{
                            width: '100%', padding: '0.6rem', background: '#0B0B10', color: '#FFFFFF',
                            border: '1px solid rgba(255,138,61,0.2)', borderRadius: '8px', fontSize: '0.9rem',
                            resize: 'vertical', boxSizing: 'border-box',
                          }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => setShowReportModal(false)}
                          style={{
                            padding: '0.5rem 1.2rem', background: 'transparent', color: '#8b9cb3',
                            border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', cursor: 'pointer',
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={submitReport}
                          disabled={reportSubmitting}
                          style={{
                            padding: '0.5rem 1.2rem', background: '#FF8A3D', color: '#0B0B10',
                            border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600,
                            opacity: reportSubmitting ? 0.6 : 1,
                          }}
                        >
                          {reportSubmitting ? 'Submitting...' : 'Submit Report'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'members' && (
          <div>
            {!user ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 138, 61, 0.15)'
              }}>
                <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>👥</div>
                <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>Sign in to see members</h3>
                <p style={{ color: '#9ca3af', marginBottom: '1rem' }}>See who else is running this race and connect with them.</p>
                <button onClick={() => onLoginRequired && onLoginRequired()} style={{ padding: '0.6rem 1.5rem', background: '#FF8A3D', border: 'none', color: '#0B0B10', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}>Sign In</button>
              </div>
            ) : (
              <>
                <UserSearch onViewProfile={onViewProfile} currentUserId={user.id} />

                {/* Runners You Follow */}
                {followedRunners.length > 0 && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 style={{
                      color: '#FF8A3D',
                      fontSize: '1rem',
                      marginBottom: '0.75rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}>
                      Runners You Follow
                    </h4>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.5rem',
                      padding: '1rem',
                      background: 'rgba(255, 138, 61, 0.05)',
                      border: '1px solid rgba(255, 138, 61, 0.2)',
                      borderRadius: '12px',
                    }}>
                      {followedRunners.map(runner => (
                        <UserCard
                          key={runner.id}
                          user={runner}
                          isFollowing={true}
                          onToggleFollow={handleToggleFollow}
                          onViewProfile={onViewProfile}
                          tags={memberTags[runner.id] || []}
                          currentUserId={user.id}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* All Members */}
                <h4 style={{ color: '#FFFFFF', fontSize: '1rem', marginBottom: '0.75rem' }}>
                  All Registered Runners ({members.length})
                </h4>
                {membersLoading ? (
                  <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>Loading members...</p>
                ) : members.length === 0 ? (
                  <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>No one has registered yet. Be the first!</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {members.map(member => (
                      <UserCard
                        key={member.id}
                        user={member}
                        isFollowing={followingSet.has(member.id)}
                        onToggleFollow={handleToggleFollow}
                        onViewProfile={onViewProfile}
                        tags={memberTags[member.id] || []}
                        currentUserId={user.id}
                        charityName={member.charity_name}
                        fundraisingUrl={member.fundraising_url}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'stories' && (
          <RaceStories
            raceId={community.id}
            raceName={community.name}
            user={user}
            onViewProfile={onViewProfile}
            onLoginRequired={onLoginRequired}
          />
        )}

        {activeTab === 'reviews' && (
          <RaceReviews
            raceId={community.id}
            user={user}
            onLoginRequired={onLoginRequired}
          />
        )}

        {activeTab === 'message-boards' && (
          <div>
            <h3 style={{
              color: '#FFFFFF',
              fontSize: '1.5rem',
              marginBottom: '1rem'
            }}>
              Discussion Boards
            </h3>
            {!communityId ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem',
                background: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 138, 61, 0.15)'
              }}>
                <p style={{ color: '#8b9cb3' }}>Register for this race to access its message boards.</p>
              </div>
            ) : boardsLoading ? (
              <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>Loading boards...</p>
            ) : (
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 138, 61, 0.15)',
                borderRadius: '16px',
                overflow: 'hidden',
                padding: '0.5rem'
              }}>
                {boards.length === 0 ? (
                  <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '1.5rem', margin: 0 }}>
                    No boards yet for this community.
                  </p>
                ) : (
                  boards.map((board) => (
                    <div
                      key={board.id}
                      onClick={() => setSelectedBoard(board)}
                      style={{
                        padding: '1rem 1.25rem',
                        margin: '0.25rem',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <span style={{ fontSize: '1.25rem' }}>{board.icon || '\u{1F4AC}'}</span>
                        <div>
                          <h4 style={{
                            color: '#FFFFFF',
                            margin: 0,
                            fontSize: '1rem',
                            fontWeight: 500
                          }}>
                            {board.name}
                          </h4>
                          <p style={{
                            color: '#6b7280',
                            margin: '0.15rem 0 0 0',
                            fontSize: '0.8rem'
                          }}>
                            {board.thread_count} {board.thread_count === 1 ? 'thread' : 'threads'}
                          </p>
                        </div>
                      </div>
                      <span style={{ color: '#FF8A3D', fontSize: '1rem' }}>{'\u2192'}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityDetail;